import { apiClient as api } from './api';
import {
  BreakdancePromptRequest,
  BreakdanceSession,
  BreakdanceIteration,
  DiscoCulturePromptRequest,
  DiscoCultureSession,
  DiscoCultureIteration,
  StockPhotoTechnologyPromptRequest,
  StockPhotoTechnologySession,
  StockPhotoTechnologyIteration,
  UrbanCulturePromptRequest,
  UrbanBikeAutoLifeSession,
  UrbanBikeAutoLifeIteration,
  EnhanceRequest,
  EnhanceDiscoRequest,
  StockPhotoEnhancementRequest,
  EnhanceUrbanRequest
} from '../models/newNicheAgents';

const SESSION_STORAGE_KEY = 'new_niche_agent_sessions';

// Helper functions for session management
const saveSession = (sessionId: string, sessionData: any): void => {
  const sessions = JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) || '{}');
  sessions[sessionId] = {
    ...sessionData,
    lastUpdated: new Date().toISOString()
  };
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
};

const getSession = (sessionId: string): any => {
  const sessions = JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) || '{}');
  return sessions[sessionId];
};

const getAllSessions = (): Record<string, any> => {
  return JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) || '{}');
};

const deleteSession = (sessionId: string): void => {
  const sessions = JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) || '{}');
  delete sessions[sessionId];
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessions));
};

// Breakdance Agent Service
export const breakdanceService = {
  async generate(request: BreakdancePromptRequest): Promise<BreakdanceSession> {
    const response = await api.post('/api/agents/breakdance/generate', request);
    const session: BreakdanceSession = {
      sessionId: response.data.sessionId || Date.now().toString(),
      currentPrompt: response.data.generatedPrompt || response.data.prompt || '',
      iterations: [{
        iterationNumber: 1,
        generatedPrompt: response.data.generatedPrompt || response.data.prompt || '',
        timestamp: new Date().toISOString()
      }],
      metadata: response.data
    };
    saveSession(session.sessionId, session);
    return session;
  },

  async enhance(sessionId: string, request: EnhanceRequest): Promise<BreakdanceIteration> {
    const response = await api.post(`/api/agents/breakdance/session/${sessionId}/enhance`, request);
    const session = getSession(sessionId);
    if (session) {
      const iteration: BreakdanceIteration = {
        iterationNumber: session.iterations.length + 1,
        generatedPrompt: response.data.enhancedPrompt || response.data.generatedPrompt || '',
        enhancementFocus: request.enhancementFocus,
        timestamp: new Date().toISOString()
      };
      session.iterations.push(iteration);
      session.currentPrompt = iteration.generatedPrompt;
      saveSession(sessionId, session);
      return iteration;
    }
    throw new Error('Session not found');
  }
};

// Disco Culture Agent Service
export const discoCultureService = {
  async generate(request: DiscoCulturePromptRequest): Promise<DiscoCultureSession> {
    const response = await api.post('/api/agents/culture-disco/generate', request);
    const session: DiscoCultureSession = {
      sessionId: response.data.sessionId || Date.now().toString(),
      currentPrompt: response.data.generatedPrompt || response.data.prompt || '',
      iterations: [{
        iterationNumber: 1,
        generatedPrompt: response.data.generatedPrompt || response.data.prompt || '',
        culturalContext: response.data.culturalContext,
        timestamp: new Date().toISOString()
      }],
      metadata: response.data
    };
    saveSession(session.sessionId, session);
    return session;
  },

  async enhance(sessionId: string, request: EnhanceDiscoRequest): Promise<DiscoCultureIteration> {
    const response = await api.post(`/api/agents/culture-disco/session/${sessionId}/enhance`, request);
    const session = getSession(sessionId);
    if (session) {
      const iteration: DiscoCultureIteration = {
        iterationNumber: session.iterations.length + 1,
        generatedPrompt: response.data.enhancedPrompt || response.data.generatedPrompt || '',
        culturalContext: response.data.culturalContext,
        timestamp: new Date().toISOString()
      };
      session.iterations.push(iteration);
      session.currentPrompt = iteration.generatedPrompt;
      saveSession(sessionId, session);
      return iteration;
    }
    throw new Error('Session not found');
  }
};

// Stock Photo Technology Agent Service
export const stockPhotoTechnologyService = {
  async generate(request: StockPhotoTechnologyPromptRequest): Promise<StockPhotoTechnologySession> {
    const response = await api.post('/api/agents/stock-photo-technology/generate', request);
    const session: StockPhotoTechnologySession = {
      sessionId: response.data.sessionId || Date.now().toString(),
      currentPrompt: response.data.generatedPrompt || response.data.prompt || '',
      iterations: [{
        iterationNumber: 1,
        generatedPrompt: response.data.generatedPrompt || response.data.prompt || '',
        technicalDetails: response.data.technicalDetails,
        timestamp: new Date().toISOString()
      }],
      metadata: response.data
    };
    saveSession(session.sessionId, session);
    return session;
  },

  async enhance(sessionId: string, request: StockPhotoEnhancementRequest): Promise<StockPhotoTechnologyIteration> {
    const response = await api.post(`/api/agents/stock-photo-technology/session/${sessionId}/enhance`, request);
    const session = getSession(sessionId);
    if (session) {
      const iteration: StockPhotoTechnologyIteration = {
        iterationNumber: session.iterations.length + 1,
        generatedPrompt: response.data.enhancedPrompt || response.data.generatedPrompt || '',
        technicalDetails: response.data.technicalDetails,
        timestamp: new Date().toISOString()
      };
      session.iterations.push(iteration);
      session.currentPrompt = iteration.generatedPrompt;
      saveSession(sessionId, session);
      return iteration;
    }
    throw new Error('Session not found');
  }
};

// Urban Bike Auto Life Agent Service
export const urbanBikeAutoLifeService = {
  async generate(request: UrbanCulturePromptRequest): Promise<UrbanBikeAutoLifeSession> {
    const response = await api.post('/api/agents/urban-bike-auto-life/generate', request);
    const session: UrbanBikeAutoLifeSession = {
      sessionId: response.data.sessionId || Date.now().toString(),
      currentPrompt: response.data.generatedPrompt || response.data.prompt || '',
      iterations: [{
        iterationNumber: 1,
        generatedPrompt: response.data.generatedPrompt || response.data.prompt || '',
        culturalElements: response.data.culturalElements,
        timestamp: new Date().toISOString()
      }],
      metadata: response.data
    };
    saveSession(session.sessionId, session);
    return session;
  },

  async enhance(sessionId: string, request: EnhanceUrbanRequest): Promise<UrbanBikeAutoLifeIteration> {
    const response = await api.post(`/api/agents/urban-bike-auto-life/session/${sessionId}/enhance`, request);
    const session = getSession(sessionId);
    if (session) {
      const iteration: UrbanBikeAutoLifeIteration = {
        iterationNumber: session.iterations.length + 1,
        generatedPrompt: response.data.enhancedPrompt || response.data.generatedPrompt || '',
        culturalElements: response.data.culturalElements,
        timestamp: new Date().toISOString()
      };
      session.iterations.push(iteration);
      session.currentPrompt = iteration.generatedPrompt;
      saveSession(sessionId, session);
      return iteration;
    }
    throw new Error('Session not found');
  }
};

// Common session management functions
export const newNicheAgentsSessionService = {
  getAllSessions,
  getSession,
  saveSession,
  deleteSession,
  getAllSessionIds: () => Object.keys(getAllSessions()),
  getSessionsByAgent: (agentType: string) => {
    const sessions = getAllSessions();
    return Object.entries(sessions)
      .filter(([_, session]) => session.agentType === agentType)
      .map(([id, _]) => id);
  }
};